import {
  Outlet,
  useLocation,
  useNavigate,
  Link,
  useParams,
} from "react-router-dom";
import { Helmet } from "react-helmet-async";
import NavigateNext from "@mui/icons-material/NavigateNext";
import {
  Box,
  Breadcrumbs,
  Card,
  CircularProgress,
  Link as MuiLink,
  Stack,
  Tab,
  Tabs,
  TabsProps,
  Typography,
} from "@mui/material";
import { useQuery } from "react-query";

import { getDocumentTitle, useRequireParams } from "@/utils";
import { getMember } from "@/services/member";
import { MemberProfile } from "@/features/member/pages/MemberProfilePage/MemberProfile";
import { MemberProduct } from "@/features/member/pages/MemberProfilePage/MemberProduct";
import { CustomerRemark } from "@/features/member/pages/MemberProfilePage/CustomerRemark";

export function useMember(id: string) {
  return useQuery(["member", id], () => getMember(+id));
}

export function MemberProfilePage() {
  const { memberId, saleId } = useRequireParams(["memberId", "saleId"]);
  const { opportunityId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const path = location?.pathname ?? "";
  const isOpportunity = path.includes("opportunities");

  const memberListPath = isOpportunity
    ? `/sales/${saleId}/opportunities/${opportunityId ?? ""}/members`
    : "/sales";

  const breadcrumbs = isOpportunity
    ? [
        { label: "ฝ่ายขาย", to: "/sales" },
        { label: "ข้อมูลฝ่ายขาย", to: `/sales/${saleId}` },
        { label: "รายการเพิ่มยอดขาย", to: `/sales/${saleId}/opportunities` },
        {
          label: "รายละเอียดลูกค้า",
          to: `/sales/${saleId}/opportunities/${opportunityId ?? ""}`,
        },
      ]
    : [
        { label: "ฝ่ายขาย", to: ".." },
        {
          label: "ข้อมูลฝ่ายขาย",
          to: `/sales/${saleId}`,
        },
      ];

  const onChangeTab: TabsProps["onChange"] = (_, value: string) => {
    navigate(value, { replace: true });
  };

  const { data: profileData, isFetching } = useMember(memberId);

  const isLoaded = !isFetching && typeof profileData !== "undefined";
  const title = profileData?.code ?? "";

  return (
    <Box>
      <Helmet>
        <title>{getDocumentTitle(title)}</title>
      </Helmet>
      <Breadcrumbs
        separator={<NavigateNext fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 2 }}
      >
        {breadcrumbs.map(({ label, to }) => (
          <MuiLink key={label} component={Link} color="text.secondary" to={to}>
            {label}
          </MuiLink>
        ))}
        <Typography color="text.primary">ข้อมูลสมาชิก</Typography>
      </Breadcrumbs>
      <Stack spacing={3}>
        {isLoaded ? (
          <>
            <MemberProfile data={profileData} />
            {profileData.customerId && (
              <CustomerRemark
                data={profileData.latestRemark}
                customerId={profileData.customerId}
                queryKey={`member_${memberId}`}
              />
            )}
          </>
        ) : (
          <Card>
            <Box display="grid" sx={{ placeItems: "center" }} height={1192}>
              <CircularProgress disableShrink />
            </Box>
          </Card>
        )}
        <MemberProduct data={profileData} isLoading={isFetching} />
        <Box>
          <Tabs value={path} sx={{ mb: 2 }} onChange={onChangeTab}>
            <Tab
              label="ประวัติการจองคลาส"
              value={`${memberListPath}/${saleId}/member/${memberId}`}
              id="booking"
            />
            <Tab
              label="ประวัติการจองเทรนเนอร์"
              value={`${memberListPath}/${saleId}/member/${memberId}/pt`}
            />
            <Tab
              label="ประวัติการชำระเงิน"
              value={`${memberListPath}/${saleId}/member/${memberId}/transactions`}
            />
          </Tabs>
          <Outlet />
        </Box>
      </Stack>
    </Box>
  );
}
